
import { Moon, Sun } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

/**
 * Page-level theme switch. The document follows the system preference until the
 * reader overrides it here; the override is stamped on <html data-theme> so the
 * whole page flips together and no section ever inverts on its own.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useState<'light' | 'dark' | null>(null);

  useEffect(() => {
    const stored = window.localStorage.getItem('shopace.theme');
    if (stored === 'light' || stored === 'dark') setTheme(stored);
  }, []);

  const toggle = () => {
    const current =
      theme ??
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    const next = current === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.dataset.theme = next;
    window.localStorage.setItem('shopace.theme', next);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className="grid size-12 place-items-center rounded-[10px] text-ink transition-colors hover:bg-raised"
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {theme === 'dark' ? (
        <Sun size={20} weight="bold" aria-hidden />
      ) : (
        <Moon size={20} weight="bold" aria-hidden />
      )}
    </button>
  );
}

/**
 * Applies the stored override before first paint so a dark-theme reader never
 * sees a white flash. Runs once, inline, in <head>.
 */
export function ThemeScript() {
  const js = `try{var t=localStorage.getItem('shopace.theme');if(t==='dark'||t==='light'){document.documentElement.dataset.theme=t}}catch(e){}`;
  return <script dangerouslySetInnerHTML={{ __html: js }} />;
}
